import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Disable,
  ErrorMessage,
  ShowIf,
  SinglePageable,
  SinglePageableResponse,
} from "../../../core/component";
import { Button, Empty, Flex, Form, message, Modal } from "antd";
import { confirm, notify, translate } from "../../../core";
import {
  CloseOutlined,
  CopyOutlined,
  DeleteOutlined,
  FolderOpenOutlined,
  HistoryOutlined,
  PlusOutlined,
  SaveFilled,
  SearchOutlined,
  StopOutlined,
} from "@ant-design/icons";
import {
  BadRequestException,
  EntryMode,
  Nullable,
  PageableRequest,
  PageContextValue,
  QueryPageableRequest,
  QueryPageableResponse,
} from "../../../core/type";
import { Item, ItemEntry, ItemType } from "../type";
import { ItemForm } from "./ItemForm";
import { StonePasswordModal } from "./StonePasswordModal";
import { ItemPageContext } from "./ItemPageContext";
import { ItemSearch } from "./ItemSearch";
import styles from "./ItemPage.module.scss";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useItemData } from "../hook/item-data-hook";
import { GetItemPage, GetItemsForPage, ItemArchive, ItemRollBack } from "../graphql/item.graphql";
import { ItemFindRequest } from "../type/item-query";
import { ItemPagePrintButton } from "./ItemPagePrintButton";
import { ItemAdvancedSearch } from './ItemAdvancedSearch'
import { ReportPdfViewer, ReportPdfViewerProps } from '../../../core/component/Report'
import { useNavigate, useParams } from 'react-router-dom'

type PdfProps = Pick<ReportPdfViewerProps, "name" | "title" | "params">

export const ItemPage = () => {
  const [sending, setSending] = useState<boolean>(false);
  const [entity, setEntity] = useState<Item>();
  const [page, setPage] = useState<number | null>();
  const [errors, setErrors] = useState<string[]>([]);
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState<boolean>(false)
  const [form] = Form.useForm();
  const [entryMode, setEntryMode] = useState<EntryMode>(EntryMode.Nothing);
  const [sendingDuplicate, setSendingDuplicate] = useState<boolean>(false);
  const {init, toDto, create, update, remove, duplicate} = useItemData();
  const [pdfProps, setPdfProps] = useState<Nullable<PdfProps>>(null)
  const [find, {loading}] = useLazyQuery<
    QueryPageableResponse<Item, "itemsFind">,
    QueryPageableRequest<ItemFindRequest>
  >(GetItemsForPage);
  const [findRequest, setFindRequest] = useState<ItemFindRequest>();
  const [findPage] = useLazyQuery(GetItemPage);
  const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
  const {sku} = useParams<{ sku?: string }>()
  const [messageApi, contextHolder] = message.useMessage()
  const navigate = useNavigate();
  const [data, setData] = useState<Item | null>(null);
  const [passwordOpen, setPasswordOpen] = useState<boolean>(false);
  const [pendingEntry, setPendingEntry] = useState<Nullable<ItemEntry>>(null);

  useQuery(GetItemPage, {
    variables: {
      request: {
        sku,
      },
    },
    skip: !sku,
    onCompleted: dataPage => {
      if (data?.sku !== sku) {
        fetch({}, dataPage.itemPage)
      }
    }
  })

  const [archive, {loading: archiving}] = useMutation(ItemArchive, {
    variables: {
      id: entity?.id ?? 0
    },
    onCompleted: () => messageApi.success(translate('archiving', 'completed_success_message')),
    onError: error => messageApi.error(error.message),
  })

  const [rollBack, {loading: rollBacking}] = useMutation(ItemRollBack, {
    variables: {
      id: entity?.id ?? 0
    },
    onCompleted: () => messageApi.success(translate('roll_backing', 'completed_success_message')),
    onError: error => messageApi.error(error.message),
  })

  useEffect(() => {
    setIsFormChanged(false);
  }, [entity]);

  const onEntityChange = async (
    entity: Item | null,
    mode: EntryMode | null = null
  ) => {
    if (entity) {
      setEntryMode(EntryMode.Modify);
    } else {
      if (mode === EntryMode.New) {
        form.resetFields();
        setEntryMode(EntryMode.New);
        entity = await init();
      }
    }

    setEntity(entity || undefined);
  };

  const save = async (entry: ItemEntry) => {
    const isExistingStone = !!entity?.id && entity?.type === ItemType.Stone;

    if (isExistingStone) {
      setPendingEntry(entry);
      setPasswordOpen(true);
      return;
    }

    await persist(entry);
  };

  const persist = async (entry: ItemEntry) => {
    setErrors([]);
    const id = entity?.id;

    setSending(true);

    const dto = toDto(entry);

    try {
      if (id) await update(id, dto);
      else {
        const result = await create(dto);
        if (entity) entity.id = result.id;
      }

      notify.success(translate("item"), translate("save_success_message"));

      await refresh();
    } catch (e) {
      if (e instanceof BadRequestException) setErrors(e.message);
    } finally {
      setSending(false);
    }
  };

  const handleDuplicate = async () => {
    if (!entity) return;

    const result = await confirm(translate("copy_item_confirmation"));

    if (!result) return;

    setSendingDuplicate(true);

    try {
      const result = await duplicate(entity?.id);
      const {data: pageResponse} = await findPage({
        variables: {
          request: {
            id: result?.id,
          },
        },
      });
      return fetch({}, pageResponse?.itemPage);
    } catch (e) {
      if (e instanceof BadRequestException) setErrors(e.message);
    } finally {
      setSendingDuplicate(false);
    }
  };

  const handleRemove = async () => {
    if (!entity) return;

    const result = await confirm(
      translate("remove_confirmation_message", "item", "?")
    );

    if (!result) return;

    await remove(entity.id);
    close();

    notify.success(
      translate("remove", "item"),
      translate("the", "item", "remove_success_message")
    );
  };

  const onChange = async (
    request: PageableRequest,
    page: number
  ): Promise<SinglePageableResponse<Item>> => {
    const {data: result} = await find({
      variables: {
        request: {
          ...request,
          ...findRequest,
        },
      },
    });

    setData(result?.itemsFind.data?.[0] || null);
    const [entity] = result?.itemsFind.data || [];
    navigate(`/inventory/${entity?.sku}`);
    await onEntityChange(entity);

    if (!page) setPage(1);

    return {
      count: result?.itemsFind.count || 0,
    };
  };

  const fetch = (request: ItemFindRequest, page?: number) => {
    setFindRequest(request);
    changePage(0);
    setTimeout(() => {
      changePage(page);
    }, 500);
  };

  const changePage = (page: number = 1) => {
    setPage(0);
    setTimeout(() => setPage(page), 500);
  };

  const close = () => {
    setEntity(undefined);
    setEntryMode(EntryMode.Nothing);
  };

  const onPasswordSuccess = async () => {
    const entry = pendingEntry;
    setPasswordOpen(false);
    setPendingEntry(null);
    if (entry) await persist(entry);
  };

  const onPasswordCancel = () => {
    setPasswordOpen(false);
    setPendingEntry(null);
  };

  const refresh = async () => {
    const {data: result} = await find({
      variables: {
        request: {
          id: entity?.id,
          take: 1,
          skip: 0,
        },
      },
    });
    const [item] = result?.itemsFind.data || [];
    await onEntityChange(item);
  };

  const contextValue = useMemo<PageContextValue<Item, ItemEntry>>(
    () => ({
      form,
      save,
      entity,
      refresh,
      fetch: fetch,
      onFormChange: () => setIsFormChanged(true),
    }),
    [form, save, entity, refresh, fetch]
  );

  return (
    <ItemPageContext.Provider value={contextValue}>
      <Container
        leftClassName={`action-container ${styles.toolbarButtons}`}
        paddingClass="0"
        loading={loading}
        toolbarItems={{
          start: [
            <div key="search-add-group" className="button-group">
              <Button
                key={translate("search")}
                onClick={() => setSearchOpen(true)}
                icon={<SearchOutlined/>}
                className="btn-primary-gradient"
              >
                {translate("search")}
              </Button>

              <Button
                key={translate('advanced', "search")}
                onClick={() => setAdvancedSearchOpen(true)}
                icon={<SearchOutlined/>}
                className="btn-primary-gradient"
              >
                {translate('advanced', "search")}
              </Button>

              <Button
                onClick={() => onEntityChange(null, EntryMode.New)}
                icon={<PlusOutlined/>}
                key={translate("add")}
                className="btn-primary-gradient"
              >
                {translate("add")}
              </Button>
            </div>,

            <div key="save-remove-clear-group" className="button-group">
              <Button
                loading={sending}
                onClick={() => form.submit()}
                disabled={!entity || entity?.archived}
                type={isFormChanged ? "primary" : "default"}
                icon={<SaveFilled/>}
                key={translate("save")}
                className={isFormChanged ? "btn-primary-gradient" : ""}
              >
                {translate("save")}
              </Button>
              <Button
                danger
                className="danger-button"
                disabled={!entity?.id || entity?.archived}
                onClick={handleRemove}
                icon={<DeleteOutlined/>}
                key={translate("remove")}
              >
                {translate("remove")}
              </Button>
              <Button
                disabled={entryMode === EntryMode.Nothing}
                onClick={async () => {
                  if (entryMode === EntryMode.New) {
                    form.resetFields();
                    setIsFormChanged(false);
                  }
                  if (entryMode === EntryMode.Modify) await refresh();
                }}
                icon={<StopOutlined/>}
                key={translate("clear")}
              >
                {translate("clear")}
              </Button>
            </div>,

            <div key="actions-group" className="button-group">
              <Button
                loading={sendingDuplicate}
                disabled={entryMode === EntryMode.Nothing}
                onClick={handleDuplicate}
                icon={<CopyOutlined/>}
                key={translate("copy")}
              >
                {translate("copy")}
              </Button>
              <Button
                key={translate("history")}
                disabled={!entity?.id}
                onClick={() => {
                  setPdfProps({
                    name: 'order-item-history',
                    title: translate('ordered', 'inventory', 'history'),
                    params: {
                      itemId: entity?.id,
                    },
                  });
                }}
                icon={<HistoryOutlined/>}
              >
                {translate("history")}
              </Button>
              <ItemPagePrintButton item={entity}/>
            </div>,

            <div key="exit-group" className="button-group">
              <Button
                key={translate("exit")}
                onClick={close}
                icon={<CloseOutlined/>}
              >
                {translate("exit")}
              </Button>
              <Button
                key='archive'
                disabled={!entity?.id}
                loading={archiving || rollBacking}
                onClick={async () => {
                  Modal.confirm({
                    title: translate(
                      entity?.archived ? 'roll_back' : 'archive',
                      'item',
                      `#${entity?.sku}`
                    ),
                    content: translate("sure_message"),
                    onOk: async () => {
                      entity?.archived
                        ? await rollBack()
                        : await archive()
                      await refresh()
                    }
                  })
                }}
              >
                {translate(entity?.archived ? 'roll_back': 'archive')}
              </Button>
            </div>,
          ],
          navigation: [
            <SinglePageable
              key={"pagination"}
              onChange={onChange}
              defaultPage={page}
            />,
          ],
        }}
      >
        <ItemSearch
          open={searchOpen}
          closeMe={() => setSearchOpen(false)}
        />

        <ItemAdvancedSearch
          closeMe={() => setAdvancedSearchOpen(false)}
          open={advancedSearchOpen}
        />

        {entity ? (
          <div
            className={[
              "print-hidden mt-2 bg-gray",
              styles["inventoryEntryForm"],
            ].join(" ")}
          >
            <ErrorMessage
              title={translate("entry_fail_message", "inventory")}
              message={errors}
            />

            <ShowIf
              condition={!!entity?.archived}
              replacement={<></>}
            >
              <Flex justify='center'>
                <div className='p-1 text-center' style={{border: 'solid 1px #ff4d4f', width: 100}}>
                  <span className='text-uppercase' style={{color: '#ff4d4f', fontSize: 18}}>{translate('archived')}</span>
                </div>
              </Flex>
            </ShowIf>
            <Disable disable={!!entity?.archived}>
              <ItemForm/>
            </Disable>

          </div>
        ) : (
          <Empty className="mt-4"/>
        )}
      </Container>

      {entity && pdfProps ? (
        <ReportPdfViewer
          {...pdfProps}
          open={!!pdfProps}
          onCancel={() => setPdfProps(null)}
        />
      ) : (
        ''
      )}
      <StonePasswordModal
        open={passwordOpen}
        onCancel={onPasswordCancel}
        onSuccess={onPasswordSuccess}
      />

      {contextHolder}
    </ItemPageContext.Provider>
  );
};
